import React from "react"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import Image, { ImagePropTypes } from "@src/components/core-image"
import ImageWrapper from "@src/components/core-image-wrapper"
import Distance from "@src/components/core-value-local-distance"
import HtmlContent from "@src/components/core-value-html"
import IconDetailContentTile from "@src/components/core-icon-detail-tile"
import DifficultyRating from "@src/components/core-value-difficulty-rating"
import DifficultyRatingSymbol from "@src/components/core-value-difficulty-rating-symbol"
import ClickableTileWrapper from "@src/components/core-clickable-tile-wrapper"
import TrailCard from "@src/components/trail-card"

const RouteImage = styled(ImageWrapper)`
  ${props =>
    ["map", "popup"].includes(props.$variant)
      ? css`
          flex: 0 0 120px;
        `
      : css`
          @media only screen and (${props => props.theme.screen.tablet.max}) {
            height: 185px;
            max-width: 768px;
          }
          @media only screen and (${props => props.theme.screen.small.min}) {
            flex: 0 0 360px;
          }
        `}
`
const StyledTitle = styled.h4`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  line-height: 24px;
`
const Description = styled(HtmlContent)`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 0 1px;
  font-size: 12px;
  line-height: 14px;

  & p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }

  ${props =>
    !["map", "popup"].includes(props.$variant)
      ? css`
          @media only screen and (${props => props.theme.screen.small.min}) {
            // Reference: https://css-tricks.com/almanac/properties/l/line-clamp/
            display: -webkit-box;
            line-clamp: 3;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            white-space: normal;
            max-height: 60px;
            line-height: 20px;
            padding: 6px 0;

            // Required for line clamping to work
            & p {
              overflow: initial;
              white-space: normal;
            }
          }
        `
      : undefined}
`
const DetailValue = css`
  font-weight: 500;
  font-size: 0.75rem;

  @media only screen and (${props => props.theme.screen.small.min}) {
    font-size: 0.875rem;
  }
`
const RouteIconDetail = styled(IconDetailContentTile)`
  ${DetailValue}
  align-items: center;
  & > .circle-div {
    height: 23px;
    width: 23px;
    & .icon {
      font-size: 23px;
    }
  }
`
const RouteDistanceValue = styled(Distance)`
  ${DetailValue}
`
const RouteDistance = styled(RouteIconDetail)``
const RouteElevation = styled(RouteIconDetail)``
const RouteDifficulty = styled(RouteIconDetail)``
const RouteReviews = styled(RouteIconDetail)``
const RouteDuration = styled(RouteIconDetail)``
const DifficultyDetails = styled.div`
  ${DetailValue}

  & > span {
    margin-left: 4px;
  }

  & > .rating-square,
  > .rating-circle {
    margin: 0 2px;
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    & > .rating-square,
    & > .rating-circle {
      margin: 0 4px;
    }
  }
`
const TrailStats = styled.div``
const UserRatings = styled.div``
const RouteDetails = styled.div`
  overflow: hidden;
  padding: 8px 4px 8px 6px;

  ${props =>
    ["map", "popup"].includes(props.$variant)
      ? css`
          display: grid;
          grid-template-rows: 25px auto 25px 25px;
          grid-template-columns: auto auto;
          & > ${StyledTitle} {
            grid-row: 1 / 2;
            grid-column: 1 / 3;
          }
          & > ${Description} {
            grid-row: 2 / 3;
            grid-column: 1 / 3;
          }
          & > ${TrailStats} {
            grid-row: 3 / 5;
            grid-column: 1 / 3;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            & > ${RouteDifficulty}, & > ${RouteDistance} {
              grid-row: 1 / 2;
            }
            & > ${RouteElevation}, & > ${RouteReviews} {
              grid-row: 2 / 3;
            }
          }
        `
      : css`
          display: flex;
          flex-direction: column;
          width: calc(100%);
          padding: 15px;
          & > ${TrailStats} {
            padding: 5px 0 0 0;
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
          }
          & > ${Description} {
            padding: 5px 0;
          }
          & > ${UserRatings} {
            padding: 5px 0;
            margin: 5px 0 0 0;
            border-top: 1px solid ${props => props.theme.borderGray};
            display: flex;
            flex-direction: column;
            & > dl {
              padding: 10px 0 0 0;
              display: flex;
              justify-content: space-around;
              width: 100%;

              & > ${DifficultyDetails} {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-end;
                & > dt {
                  order: 2;
                  text-align: center;
                }
                & > dd {
                  order: 1;
                  // Required to center difficulty symbols with large rating
                  height: 28px;
                  display: flex;
                  align-items: center;
                }
                // Large rating text
                &.overall-rating {
                  & > dd {
                    font-weight: 700;
                    font-size: 1.5rem;
                  }
                }
              }
              &.empty {
                justify-content: flex-start;
                // Large rating text
                & > ${DifficultyDetails}.overall-rating > dd {
                    font-weight: normal;
                    font-size: inherit;
                }
              }
            }
          }
          @media only screen and (${props => props.theme.screen.small.min}) {
            padding: 10px;
            display: grid;
            grid-template-rows: 40px 60px 1fr 85px;
            & > ${StyledTitle} {
              font-size: 1.5rem;
              display: flex;
              align-items: center;
            }
            & > ${TrailStats} {
              & > ${RouteIconDetail} {
                & > .circle-div {
                  height: 30px;
                  width: 30px;
                  & .icon {
                    font-size: 30px;
                  }
                }
              }
            }
            & > ${UserRatings} {
              & > h5 {
                font-weight: 700;
                color: #000;
                text-align: center;
                font-feature-settings: 'clig' off, 'liga' off;

                /* Buttons/Button M */
                font-family: system-ui;
                font-size: 16px;
                font-style: normal;
                line-height: 24px; /* 150% */
              }
          }
        `}
`
const DetailTile = styled.div`
  overflow: hidden;
  background-color: ${props => props.theme.bg1};
  box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border1};
  ${props => (props.$variant === "popup" ? " width: 350px" : " width: 100%")};
  min-height: 90px;
  margin: 0;
  display: flex;

  ${props =>
    !["map", "popup"].includes(props.$variant)
      ? css`
          flex-direction: column;

          @media only screen and (${props => props.theme.screen.small.min}) {
            flex-direction: row;
            border-radius: 8px;
          }
        `
      : undefined}
`
const StyledClickableWrapper = styled(ClickableTileWrapper)`
  ${props =>
    props.$variant === "popup"
      ? css`
          max-width: 100%;
        `
      : css`
          width: 1000px;
          max-width: calc(100% - 20px);
        `}
`
const RouteGuideDetailTile = ({
  routeGuide,
  variant,
  lazyloadImages = true,
  focused,
  onClick,
  onHover,
  ...props
}) => {
  const isRouteRatingEmpty = !(
    routeGuide &&
    routeGuide.ratings &&
    routeGuide.ratings.overall
  )
  const Route = () => {
    if (variant === "trailCard") {
      return (
        <TrailCard
          title={routeGuide && routeGuide.name}
          image={routeGuide && routeGuide.images?.detailTile}
          difficulty={routeGuide && routeGuide.difficulty}
          distance={routeGuide && routeGuide.distance}
          elevation={routeGuide && routeGuide.elevation}
          duration={routeGuide && routeGuide.duration}
          link={routeGuide && routeGuide.uri}
          location={routeGuide && routeGuide.region.name}
        />
      )
    }
    return (
      <DetailTile $variant={variant} {...props}>
        <RouteImage $variant={variant}>
          <Image
            placeholder
            {...routeGuide.images?.detailTile}
            lazyload={lazyloadImages}
            artDirection
          />
        </RouteImage>
        <RouteDetails $variant={variant}>
          <StyledTitle>{routeGuide && routeGuide.name}</StyledTitle>
          <TrailStats>
            <RouteDifficulty
              $variant={variant}
              glyph="weightlifter"
              color="yellow-black"
              size="small"
            >
              {routeGuide && routeGuide.difficulty ? (
                <DifficultyRating
                  ratingNumber={Number(routeGuide.difficulty)}
                />
              ) : (
                "Not Available"
              )}
            </RouteDifficulty>
            <RouteDistance
              $variant={variant}
              glyph="distance"
              color="yellow-black"
              size="small"
            >
              <RouteDistanceValue
                value={routeGuide.distance.value && routeGuide.distance.value}
                unit={routeGuide.distance && routeGuide.distance.unit}
                decimals={1}
              />
            </RouteDistance>
            <RouteElevation
              $variant={variant}
              glyph="elevation2"
              color="yellow-black"
              size="small"
            >
              <RouteDistanceValue
                value={routeGuide.elevation && routeGuide.elevation.value}
                unit={routeGuide.elevation && routeGuide.elevation.unit}
              />
            </RouteElevation>
            {routeGuide.duration && (
              <RouteDuration
                $variant={variant}
                glyph="time2"
                color="yellow-black"
                size="small"
              >
                <span>{routeGuide.duration}</span>
              </RouteDuration>
            )}
            {["popup", "map"].includes(variant) ? (
              <RouteReviews
                $variant={variant}
                glyph="star"
                color="primary"
                size="small"
              >
                <span>
                  {isRouteRatingEmpty
                    ? "No Reviews"
                    : routeGuide.ratings.overall.toFixed(1)}
                </span>
              </RouteReviews>
            ) : undefined}
          </TrailStats>
          <Description
            $variant={variant}
            html={routeGuide && routeGuide.summary}
          />
          {!["popup", "map"].includes(variant) ? (
            <UserRatings>
              <h5>User Ratings</h5>
              <dl className={isRouteRatingEmpty ? "empty" : ""}>
                <DifficultyDetails className="overall-rating">
                  <dt>{isRouteRatingEmpty ? "" : "Overall Rating"}</dt>
                  <dd>
                    {isRouteRatingEmpty
                      ? "No Reviews"
                      : routeGuide.ratings.overall.toFixed(1)}
                  </dd>
                </DifficultyDetails>
                {routeGuide.ratings && routeGuide.ratings.technical && (
                  <DifficultyDetails>
                    <dt>Technical Difficulty</dt>
                    <dd>
                      <DifficultyRatingSymbol
                        ratingNumber={Math.round(routeGuide.ratings.technical)}
                        size="small"
                      />
                    </dd>
                  </DifficultyDetails>
                )}
                {routeGuide.ratings && routeGuide.ratings.physical && (
                  <DifficultyDetails>
                    <dt>Physical Difficulty</dt>
                    <dd>
                      <DifficultyRatingSymbol
                        ratingNumber={Math.round(routeGuide.ratings.physical)}
                        size="small"
                      />
                    </dd>
                  </DifficultyDetails>
                )}
              </dl>
            </UserRatings>
          ) : null}
        </RouteDetails>
      </DetailTile>
    )
  }
  return (
    <StyledClickableWrapper
      to={routeGuide.uri}
      onClick={onClick}
      focused={focused}
      title={`${variant === "popup" ? "Focus on" : "Open details for"} ${
        routeGuide.name
      }`}
      onHover={onHover ? () => onHover(routeGuide) : undefined}
      $variant={variant}
    >
      <Route />
    </StyledClickableWrapper>
  )
}
export const RouteGuideDetailTilePropTypes = {
  name: PropTypes.string,
  uri: PropTypes.string,
  summary: PropTypes.string,
  duration: PropTypes.string,
  ratings: PropTypes.shape({
    overall: PropTypes.number,
    technical: PropTypes.number,
    physical: PropTypes.number,
  }),
  difficulty: PropTypes.string,
  distance: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
  }),
  elevation: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
  }),
  images: PropTypes.shape({
    detailTile: PropTypes.shape({ ...ImagePropTypes }),
  }),
  region: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    name: PropTypes.string,
  }),
}
RouteGuideDetailTile.propTypes = {
  routeGuide: PropTypes.shape(RouteGuideDetailTilePropTypes),
  variant: PropTypes.oneOf(["map", "popup"]),
  lazyloadImages: PropTypes.bool,
  focused: PropTypes.bool,
  onClick: PropTypes.func,
  onHover: PropTypes.func,
}

export default RouteGuideDetailTile

export const RouteGuideDetailTileQuery = graphql`
  fragment TenAdventuresGQL_RouteGuideDetailTileQuery on TenAdventuresGQL_Route {
    name: title
    uri
    summary(format: HTML)
    duration
    ratings {
      overall
      technical
      physical
    }
    difficulty
    distance {
      value
      unit
    }
    elevation {
      value
      unit
    }
    images {
      detailTile {
        ...TenAdventuresGQL_ImageSizesQuery
      }
    }
    region {
      id: databaseId
      slug
      name
    }
  }

  fragment WpRouteGuideDetailTileQuery on WpRoute {
    name: title
    uri
    summary
    duration
    ratings {
      overall
      technical
      physical
    }
    difficulty
    distance {
      value
      unit
    }
    elevation {
      value
      unit
    }
    images {
      detailTile {
        ...WpImageSizesQuery
      }
    }
    region {
      id: databaseId
      slug
      name
    }
  }
`
